// src/pages/ReadingPage.jsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import TarotCard from '../components/TarotCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import VoiceReader from '../components/ui/VoiceReader';
import createMasterInterpretation from '../services/interpretationService';

// Renderiza markdown básico a HTML seguro
const renderMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/### (.*)/g, '<h3 class="oracle-h3">$1</h3>')
    .replace(/## (.*)/g, '<h2 class="oracle-h2">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p class="oracle-p">')
    .replace(/\n/g, '<br />')
    .replace(/^/, '<p class="oracle-p">')
    .replace(/$/, '</p>');
};

const ReadingPage = ({ reading, onSave, onBack, userDetails }) => {
  const cards = useSelector((state) => state.tarot.cards);
  const [interpretation, setInterpretation] = useState(reading.interpretation || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (reading.interpretation || reading.isAiPowered) return;
    const localInterp = createMasterInterpretation(reading, userDetails);
    setInterpretation(localInterp);
  }, [reading, userDetails]);

  const generateAiReading = async () => {
    setIsGenerating(true);
    setInterpretation('');
    setError('');
    try {
      const cardsWithDetails = reading.cards.map((card, index) => {
        const cardData = cards.find((c) => c.name === card.name);
        const meaning = card.isReversed ? cardData?.meaning_rev : cardData?.meaning_up;
        return {
          name: card.name,
          isReversed: card.isReversed,
          position: reading.spreadPositions[index],
          meaning: meaning || 'Significado no encontrado.',
        };
      });

      const response = await fetch('/api/generateInterpretation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: userDetails?.name || 'Consultante',
          userQuestion: userDetails?.question || 'Mi pregunta general',
          spreadName: reading.spreadName,
          cards: cardsWithDetails,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error desconocido del Oráculo AI');
      setInterpretation(data.interpretation);
    } catch (err) {
      console.error('Error en generateAiReading:', err);
      setError(`Error al conectar con el Oráculo: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = () => {
    const readingToSave = { ...reading, interpretation };
    onSave(readingToSave);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  return (
    <div className="page-container">
      <div className="reading-header">
        <h2 className="page-title font-cinzel">✦ {reading.spreadName} ✦</h2>
        <p className="page-subtitle">
          {reading.isAiPowered
            ? 'Una lectura profunda con el poder del Oráculo AI para ti.'
            : 'El mensaje que las cartas tienen para ti hoy.'}
        </p>
      </div>

      {/* Cards Display */}
      <div className="reading-cards-area">
        {reading.cards.map((card, index) => (
          <div key={index} className="reading-card-slot">
            <div className="reading-card-position-label font-cinzel">
              {reading.spreadPositions[index]}
            </div>
            <TarotCard card={card} isFlipped={true} />
            <p className="reading-card-name">{card.name}</p>
            {card.isReversed && <span className="reading-card-reversed">↕ Invertida</span>}
          </div>
        ))}
      </div>

      {/* AI Trigger Button */}
      {reading.isAiPowered && !interpretation && !isGenerating && !error && (
        <button
          onClick={generateAiReading}
          className="main-button primary-button oracle-trigger-btn"
        >
          <span className="oracle-btn-shimmer"></span>
          🔮 Consultar al Oráculo AI
        </button>
      )}

      {/* Loading State */}
      {isGenerating && (
        <div className="oracle-loading">
          <LoadingSpinner />
          <div className="oracle-loading-messages">
            <p className="oracle-loading-text">El Oráculo está leyendo las energías cósmicas...</p>
            <p className="oracle-loading-subtext">Esto puede tomar unos momentos</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="oracle-error">
          <p>⚠️ {error}</p>
          <button onClick={generateAiReading} className="main-button secondary-button" style={{ marginTop: '1rem' }}>
            Intentar de nuevo
          </button>
        </div>
      )}

      {/* Interpretation */}
      {interpretation && !isGenerating && (
        <div className="interpretation-container">
          <div className="interpretation-header">
            <span className="interpretation-icon">✨</span>
            <h3 className="interpretation-title font-cinzel">
              {reading.isAiPowered ? 'La Voz del Oráculo' : 'Tu Interpretación'}
            </h3>
          </div>
          <VoiceReader text={interpretation} label="Escuchar lectura" />
          <div
            className="interpretation-body"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(interpretation) }}
          />
          <div className="reading-actions">
            <button onClick={onBack} className="main-button secondary-button">
              ← Volver al Inicio
            </button>
            <button onClick={handleSave} className={`main-button ${isSaved ? 'primary-button--saved' : 'primary-button'}`}>
              {isSaved ? '✅ Guardada en Diario' : '📓 Guardar en Diario'}
            </button>
          </div>
        </div>
      )}

      {/* Back button if no interpretation yet */}
      {!interpretation && !isGenerating && !error && !reading.isAiPowered && (
        <div className="reading-actions" style={{ marginTop: '2rem' }}>
          <button onClick={onBack} className="main-button secondary-button">← Volver al Inicio</button>
        </div>
      )}
    </div>
  );
};

export default ReadingPage;
